import React from 'react'
import styled, { css } from 'styled-components'

import useReduxSelector from 'hooks/useReduxSelector'

import ButtonLink from '../../components/display/ButtonLink'
import SimpleA from '../../components/display/SimpleAnchor'
import SimpleLink from '../../components/display/SimpleLink'

import { InverseOption } from './types'

const sharedLinkStyles = css<InverseOption>`
  color: ${({ inverse, theme }) =>
    inverse ? theme['-ds-neutral-100'].value : theme['-ds-core-300'].value};

  &:link,
  &:visited,
  &:hover,
  &:active {
    color: inherit;
    text-decoration: none;
  }

  font-size: ${({ theme }) => theme['-ds-font-size-xs']['font-size'].value};
  font-weight: ${({ theme }) => theme['-ds-font-size-md']['font-weight'].value};
  line-height: 1.15;
`

export const NavLinkAnchor = styled(SimpleA)
  .withConfig({
    shouldForwardProp: (prop) => !['inverse'].includes(prop),
  })
  .attrs({ textDecoration: 'none' })<InverseOption>`
  ${sharedLinkStyles}
`

export const NavLink = styled(SimpleLink)
  .withConfig({
    shouldForwardProp: (prop) => !['inverse'].includes(prop),
  })
  .attrs({ textDecoration: 'none' })<InverseOption>`
  ${sharedLinkStyles}
`

type NavLinkOrAnchorProps = React.ComponentProps<typeof NavLink>

export const NavLinkOrAnchor: React.FC<NavLinkOrAnchorProps> = ({
  children,
  ...props
}) => {
  const forceRefresh = useReduxSelector((state) => state?.navbar?.force_refresh)

  if (forceRefresh) {
    const { to: href, ...otherProps } = props
    return (
      <NavLinkAnchor href={href} {...otherProps}>
        {children}
      </NavLinkAnchor>
    )
  }

  return <NavLink {...props}>{children}</NavLink>
}

export const NavButtonLink = styled(ButtonLink).attrs({
  ghost: true,
  rounded: true,
})<InverseOption>`
  color: ${({ inverse, theme }) =>
    inverse ? theme['-ds-neutral-100'].value : theme['-ds-core-300'].value};

  &:hover {
    ${({ inverse, theme }) =>
      inverse &&
      `
      color: ${theme['-ds-neutral-100'].value};
    `}
  }

  display: inline-block;
  padding-top: 12px;
  padding-bottom: 12px;
  text-transform: capitalize;
`

export const DropdownWrapper = styled.div`
  display: inline-block;
  height: 44px;
`

export const Divider = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid ${({ theme }) => theme['-ds-neutral-400'].value};
`

export const NavLinkLoading = styled.div`
  display: inline-block;
  width: 120px;
  height: ${({ theme }) => theme['-ds-font-size-xs']['font-size'].value};
  background-color: ${({ theme }) => theme['-ds-neutral-300'].value};
  border-radius: 6px;
`

export const DisplayOnlyAtBreakpoint = styled.div<{ vertical?: boolean }>`
  display: none;

  & > * {
    &:not(:first-child) {
      ${({ vertical }) => `
        margin-${vertical ? 'bottom' : 'left'}: 30px;
      `}
    }
  }
`
